<script>
import { required, minValue, integer } from 'vuelidate/lib/validators';
import Repositories from '@/repositories';
const ItemSuppliersRepository = Repositories.get('itemsuppliers');
import 'vue-loading-overlay/dist/vue-loading.css';
import Loading from 'vue-loading-overlay';
import scrollMixin from '@/mixins/scrollWheelMixin';
import Tooltip from '@/components/widgets/errors/validations-tooltip.vue';
import Multiselect from 'vue-multiselect';

export default {
  components: {
    Loading,
    Tooltip,
    Multiselect,
  },
  data() {
    return {
      isLoading: false,
      currentMinOrderQuantity: 0,
      itemSuppliersList: [],
      form: {
        itemSupplier: null,
        quantity: 0,
      },
    };
  },
  mixins: [scrollMixin],
  props: {
    supplierIdPublic: { type: String, default: null },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      ItemSuppliersRepository.customREST
        .getBySupplier(this.supplierIdPublic)
        .then((response) => {
          this.itemSuppliersList = response.data;
          this.isLoading = false;
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
          this.isLoading = false;
        });
    },
    wheelEditQuantity(event) {
      if (this.form.itemSupplier != null) {
        this.form.quantity = this.scrollMixinEvent(
          event,
          this.form.quantity,
          this.form.itemSupplier.orderMinimumQuantity
        );
      }
    },
    submitForm() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        let quantity = this.form.quantity;

        if (this.form.itemSupplier.itemQuantityType == 'unitary') {
          quantity = Number(quantity).toFixed(0);
        }

        if (quantity > 0) {
          let payload = {
            itemSupplier: this.form.itemSupplier,
            quantity: quantity,
            orderUnitQuantity: this.form.itemSupplier.orderUnitQuantity,
          };

          this.$emit('item-supplier-to-add', payload);

          this.form.itemSupplier = null;
          this.form.quantity = 0;

          this.$v.$reset();
        }
      }
    },
    itemSelectionChanged() {
      if (this.form.itemSupplier != null) {
        this.form.quantity = this.form.itemSupplier.orderMinimumQuantity;
        this.$nextTick(() => this.$refs.addorderquantity.focus());
      } else {
        this.form.quantity = 0;
      }
    },
  },
  validations() {
    return {
      form: this.rules,
    };
  },
  computed: {
    rules() {
      if (
        this.form.itemSupplier != null &&
        this.form.itemSupplier.quantityType == 'unitary'
      ) {
        return {
          itemSupplier: { required },
          quantity: {
            required,
            minValue: minValue(this.form.itemSupplier.orderMinimumQuantity),
            integer,
          },
        };
      } else {
        return {
          itemSupplier: { required },
          quantity: {
            required,
            minValue: minValue(
              this.form.itemSupplier?.orderMinimumQuantity ?? 0
            ),
          },
        };
      }
    },
    filteredItemsList() {
      return this.itemSuppliersList.map((x) => {
        return { ...x, codeandname: x.itemCode + ' - ' + x.itemName };
      });
    },
    quantityLabel() {
      let label = this.$t('general.qty');

      if (this.form.itemSupplier == null) return label;

      let minQuantity = this.form.itemSupplier.orderMinimumQuantity;

      if (minQuantity == null || minQuantity == 0 || minQuantity == 1) {
        return label;
      }

      return label + ' (Min: ' + minQuantity + ')';
    },
  },
};
</script>

<template>
  <div>
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <div class="d-flex">
      <b-form-group
        style="margin-bottom: 0"
        class="pr-3 flex-grow-1"
        :label="$t('items.item')"
      >
        <multiselect
          :validator="$v.form.itemSupplier"
          :class="{
            'is-invalid': $v.form.itemSupplier.$error,
          }"
          v-model="form.itemSupplier"
          @input="itemSelectionChanged"
          :options="filteredItemsList"
          :show-labels="false"
          :show-arrow="true"
          :label="'codeandname'"
          track-by="idPublic"
          :placeholder="$t('general.pickAValue')"
          ><template v-slot:noOptions>
            {{ $t('general.noResult') }}
          </template></multiselect
        >
        <Tooltip :validations="$v.form.itemSupplier" />
      </b-form-group>

      <b-form-group
        style="margin-bottom: 0"
        class="pr-3 quantity-group"
        :label="quantityLabel"
      >
        <b-form-input
          :class="{
            'is-invalid': $v.form.quantity.$error,
          }"
          :disabled="form.itemSupplier == null"
          type="number"
          ref="addorderquantity"
          :step="
            form.itemSupplier != null &&
            form.itemSupplier.itemQuantityType == 'unitary'
              ? '1'
              : '.00001'
          "
          v-model="form.quantity"
          :placeholder="$t('items.quantity')"
          @keydown.enter.prevent="submitForm"
          @wheel.prevent="wheelEditQuantity($event)"
        >
        </b-form-input>

        <Tooltip :validations="$v.form.quantity" />
      </b-form-group>

      <div class="pr-3 quantity-group" style="padding-top: 38px">
        <template
          v-if="
            form.itemSupplier != null &&
              form.itemSupplier.orderUnitQuantity > 0 &&
              form.itemSupplier.orderUnitQuantity != 1
          "
        >
          {{ form.itemSupplier.unitName }}
          {{ $t('general.of') }}
          {{ form.itemSupplier.orderUnitQuantity }}
        </template>
      </div>

      <b-form-group style="margin-bottom: 0" class="align-bottom mt-auto">
        <b-button
          @click="submitForm"
          block
          style="height: 40px; margin-top: 27.5px"
          variant="primary"
          >{{ $t('general.add') }}
        </b-button>
      </b-form-group>
    </div>
  </div>
</template>

<style scoped>
.quantity-group {
  min-width: 140px;
  max-width: 140px;
}
</style>
